/**
 * useWorkshopCreateForm — all state + side effects for the Create Workshop
 * drawer (BON-203), so the drawer + step bodies stay presentational. Mirrors
 * the venue create-form convention (useVenueCreateForm).
 *
 * Draft state is a single object split into `workshop` / `schedule`
 * sub-objects. The `schedule` sub-object also carries the recurrence controls:
 * whether the workshop repeats, how the series ends, and any series dates the
 * operator changed by hand.
 */
import { useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { WorkshopDetail } from '../../../../api/workshops';

import { useCreateWorkshop } from './useCreateWorkshop';
import {
  seriesPreviewFor,
  type RepeatChoice,
  type SeriesEdit,
  type SeriesEdits,
  type SeriesEndMode,
  type SeriesPreview,
} from './workshopCreateSeries';
import { makeSeriesEditors, regeneratesSeries } from './workshopCreateSeriesEditors';
import { emptyDraft, makeSubmit, type SubmitMode } from './workshopCreateSubmit';
import { errorsForStep } from './workshopCreateValidation';
import {
  earliestWorkshopDate,
  isPastDate,
  meetsLeadTime,
  MIN_LEAD_MESSAGE,
  PAST_DATE_MESSAGE,
} from './workshopLeadTime';
import { deriveEndTime, formatDefaultPrice } from './workshopTypeDefaults';

export interface WorkshopDraftWorkshop {
  workshopTypeId: string;
  /** Capacity as a string (raw input); coerced on submit. */
  capacity: string;
  /** Whole-dollar ticket price as a string (raw input); ×100 on submit. */
  price: string;
  /** The selected type's length in minutes, which sets every end time; null
   *  before a type is picked or for a type with no length set. */
  durationMinutes: number | null;
}

/** The workshop sub-object's typed fields — everything but the type's length. */
export type WorkshopTextKey = Exclude<keyof WorkshopDraftWorkshop, 'durationMinutes'>;

export interface WorkshopDraftSchedule {
  venueId: string;
  /** YYYY-MM-DD (native date input). */
  date: string;
  /** HH:MM (native time input). */
  start: string;
  /** HH:MM — always Start + the type's length, never typed; '' when it cannot
   *  be worked out (no start, no length, or past midnight). */
  end: string;
  /** Whether the workshop repeats, and on what cadence. */
  repeat: RepeatChoice;
  /** How a repeating schedule ends: on a date, or after N workshops. */
  endMode: SeriesEndMode;
  /** YYYY-MM-DD (native date input); used when `endMode` is `until`. */
  untilDate: string;
  /** Raw input; coerced on submit. Used when `endMode` is `count`. */
  occurrenceCount: string;
  /** Series dates changed by hand, by position in the generated list. */
  seriesEdits: SeriesEdits;
}

/** The schedule fields the operator types — not the derived end or the hand edits. */
export type ScheduleTextKey = Exclude<keyof WorkshopDraftSchedule, 'seriesEdits' | 'end'>;

export interface WorkshopDraft {
  /** Region the workshop's venue is scoped to (Location step). */
  regionId: string;
  workshop: WorkshopDraftWorkshop;
  schedule: WorkshopDraftSchedule;
}

/**
 * Per-field state for Region / Venue on the Location step (round 8):
 *   · `fixed`   — set from the page the operator launched on; rendered as
 *                 read-only TEXT, not editable.
 *   · `prefill` — pre-populated from page context but EDITABLE (global ⌘K on a
 *                 contextual page); changing region re-scopes the venue.
 *   · `open`    — enabled + empty.
 */
export type FieldMode = 'fixed' | 'prefill' | 'open';

/** How the Location step is seeded + which fields are editable (round 8). */
export interface LocationInit {
  regionId?: string;
  venueId?: string;
  regionMode?: FieldMode;
  venueMode?: FieldMode;
}

/** A workshop type's catalogue defaults, as the drawer consumes them. Every
 *  field is optional/nullable — the catalogue deliberately does not invent
 *  values, so a type may carry none of them (BON-117). */
export interface WorkshopTypeDefaults {
  capacity?: number | null;
  priceCents?: number | null;
  /** The workshop's length; every end time is Start + this. */
  durationMinutes?: number | null;
}

export interface WorkshopCreateForm {
  step: number;
  setStep: (i: number) => void;
  draft: WorkshopDraft;
  /** Selected region id (Location step). */
  regionId: string;
  /** Region field mode (`fixed` → read-only text; else editable). */
  regionMode: FieldMode;
  /** Venue field mode (`fixed` → read-only text; else editable). */
  venueMode: FieldMode;
  /** Choose the region on the Location step (re-scopes the venue). */
  setRegion: (regionId: string) => void;
  /** Choose the venue on the Location step. Pass the venue's owning region to
   *  adopt it in the same edit — a venue is never valid without its region, and
   *  making the operator pick the region afterwards would clear the venue. */
  setVenue: (venueId: string, regionId?: string) => void;
  /** Adopt `regionId` as the region owning an ALREADY-SEEDED venue: sets the
   *  region without touching the venue (contrast `setRegion`) and without
   *  marking the form dirty. The venue→region mapping only lands once the
   *  venues query resolves, so this completes the initial seed rather than
   *  editing it. */
  adoptVenueRegion: (regionId: string) => void;
  editWorkshop: (key: WorkshopTextKey, value: string) => void;
  editSchedule: (key: ScheduleTextKey, value: string) => void;
  /** Change one generated series date's date or start time by hand. */
  editOccurrence: (index: number, field: keyof SeriesEdit, value: string) => void;
  /** Put one series date back to what the cadence produced. */
  resetOccurrence: (index: number) => void;
  /** Apply a workshop type's catalogue defaults (all still editable). */
  applyTypeDefaults: (typeId: string, defaults: WorkshopTypeDefaults) => void;
  fieldError: (key: string) => string | undefined;
  /** Validate the VISIBLE step `step` (default: current). When invalid, write
   *  its inline field errors and return false; when valid, return true. Drives
   *  the validate-on-Continue model (B2) — Continue is always enabled. */
  validateStep: (step?: number) => boolean;
  /** Whether the user may jump to the VISIBLE step `target`: backward is always
   *  allowed; forward only when every step before it is valid (B2 node-gating). */
  stepReachable: (target: number) => boolean;
  /** True when any field differs from its initial value — gates the dirty-close
   *  confirmation (B3). */
  dirty: boolean;
  /** Inline date ERROR — set only for a past date, which blocks the step. */
  dateError: string | undefined;
  /** Inline date WARNING — the 6-week lead notice (BON-508). Does NOT block:
   *  the operator is told and decides, matching the publish modal (BON-469). */
  dateWarning: string | undefined;
  /** Earliest selectable date (YYYY-MM-DD) — the date picker's `min`. Today,
   *  not today + 6 weeks: the picker must not hide near-term dates (BON-508). */
  minDate: string;
  /** The dates a repeating schedule would create; null for a
   *  one-off, or until there is a first date to step from. */
  seriesPreview: SeriesPreview | null;
  /** Create the draft. `'navigate'` (default) goes to the new detail page;
   *  `'addAnother'` resets the wizard to step 1 keeping region context (R5). */
  submit: (mode?: SubmitMode) => void;
  submitting: boolean;
  submitError: string | null;
  /** Brief post-create confirmation after an "add another" (R5); else null. */
  confirmation: string | null;
}

/** Clear the errors a schedule field's change can resolve. */
function clearScheduleErrors(key: ScheduleTextKey, clearError: (key: string) => void) {
  if (key === 'date') clearError('startsAt');
  if (key === 'start') clearError('start');
  if (key === 'date' || key === 'start') clearError('endsAt');
  // Any schedule change can change what the series would be.
  clearError('recurrence');
  if (key === 'untilDate' || key === 'occurrenceCount') clearError(key);
}

/** The draft-mutation handlers (extracted to keep the hook under the line cap).
 *  Each edit also clears the matching field error. */
function makeDraftEditors(
  setDraft: Dispatch<SetStateAction<WorkshopDraft>>,
  clearError: (key: string) => void,
) {
  const editWorkshop = (key: WorkshopTextKey, value: string) => {
    setDraft((d) => ({ ...d, workshop: { ...d.workshop, [key]: value } }));
    clearError(key === 'price' ? 'priceCents' : key);
  };
  const editSchedule = (key: ScheduleTextKey, value: string) => {
    setDraft((d) => {
      const schedule = { ...d.schedule, [key]: value };
      if (regeneratesSeries(key) && d.schedule[key] !== value) schedule.seriesEdits = {};
      if (key === 'start') schedule.end = deriveEndTime(value, d.workshop.durationMinutes) ?? '';
      return { ...d, schedule };
    });
    clearScheduleErrors(key, clearError);
  };
  // Location step: pick a venue (region-scoped). When the caller knows which
  // region owns the venue, the venue selection carries the region with it — so
  // a venue seeded/picked on a region-less form doesn't leave Region blank for
  // the operator to fill in by hand (which would clear the venue again).
  const setVenue = (venueId: string, regionId?: string) => {
    setDraft((d) => withVenue(d, venueId, regionId));
    clearError('venueId');
    if (regionId) clearError('regionId');
  };
  const applyTypeDefaults = (typeId: string, defaults: WorkshopTypeDefaults) => {
    const durationMinutes = defaults.durationMinutes ?? null;
    setDraft((d) => ({
      ...d,
      schedule: { ...d.schedule, end: deriveEndTime(d.schedule.start, durationMinutes) ?? '' },
      workshop: {
        workshopTypeId: typeId,
        durationMinutes,
        capacity:
          defaults.capacity !== null && defaults.capacity !== undefined
            ? String(defaults.capacity)
            : d.workshop.capacity,
        price:
          defaults.priceCents !== null && defaults.priceCents !== undefined
            ? formatDefaultPrice(defaults.priceCents)
            : d.workshop.price,
      },
    }));
    clearError('workshopTypeId');
  };
  // Choosing a region clears any venue already picked (venues are
  // region-scoped, so a region change invalidates the selection).
  const setRegion = (regionId: string) => {
    setDraft((d) => ({
      ...d,
      regionId,
      schedule: { ...d.schedule, venueId: '' },
    }));
    clearError('regionId');
    clearError('venueId');
  };
  return {
    editWorkshop,
    editSchedule,
    ...makeSeriesEditors(setDraft, clearError),
    applyTypeDefaults,
    setRegion,
    setVenue,
  };
}

/** The inline Schedule-step messages + the date picker's min. Extracted to
 *  keep the hook under the line cap. A past date is an ERROR (blocks); the
 *  6-week lead is a WARNING (does not) — see workshopLeadTime (BON-508). */
function useScheduleErrors(schedule: WorkshopDraftSchedule) {
  const dateError = useMemo(
    () => (schedule.date && isPastDate(schedule.date) ? PAST_DATE_MESSAGE : undefined),
    [schedule],
  );
  // Only warn about the lead time once the date is otherwise valid — a past
  // date already has a blocking message and does not need a second one.
  const dateWarning = useMemo(() => {
    if (!schedule.date || isPastDate(schedule.date)) return undefined;
    return meetsLeadTime(schedule.date) ? undefined : MIN_LEAD_MESSAGE;
  }, [schedule]);
  const minDate = useMemo(() => earliestWorkshopDate(), []);
  return { dateError, dateWarning, minDate };
}

interface StepNavArgs {
  step: number;
  draft: WorkshopDraft;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string>>>;
}

/** The B2 step-nav pair: `validateStep` (writes the visible step's inline
 *  errors, returns valid) + `stepReachable` (forward only when all prior steps
 *  validate; backward always). Extracted to keep the hook under the line cap. */
function makeStepNav({ step, draft, setFieldErrors }: StepNavArgs) {
  const validateStep = (target = step): boolean => {
    const errs = errorsForStep(target, draft);
    setFieldErrors((fe) => ({ ...fe, ...errs }));
    return Object.keys(errs).length === 0;
  };
  const stepReachable = (target: number): boolean => {
    if (target <= step) return true;
    for (let i = 0; i < target; i += 1) {
      if (Object.keys(errorsForStep(i, draft)).length > 0) return false;
    }
    return true;
  };
  return { validateStep, stepReachable };
}

/**
 * Put a venue on the draft, adopting the venue's region when it differs (BON-203).
 *
 * Nothing else in the draft is region-scoped now the Teacher step is gone
 * (BON-466), so adopting a region resets nothing alongside it. Pure, and kept
 * out of the hook so `useWorkshopCreateForm` stays inside the size cap.
 */
function withVenue(d: WorkshopDraft, venueId: string, regionId?: string): WorkshopDraft {
  const schedule = { ...d.schedule, venueId };
  if (!regionId || regionId === d.regionId) return { ...d, schedule };
  return { ...d, regionId, schedule };
}

export function useWorkshopCreateForm(
  onCreated: (workshop: WorkshopDetail) => void,
  location: LocationInit = {},
): WorkshopCreateForm {
  const regionMode = location.regionMode ?? 'open';
  const venueMode = location.venueMode ?? 'open';

  const [step, setStep] = useState(0);
  const initialDraft = useRef(emptyDraft(location.regionId ?? '', location.venueId ?? ''));
  const [draft, setDraft] = useState<WorkshopDraft>(() => initialDraft.current);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const mutation = useCreateWorkshop();

  const clearError = (key: string) => setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: '' } : fe));

  const editors = makeDraftEditors(setDraft, clearError);

  // Completes the seed for a venue that arrived without its region (e.g. the
  // drawer launched from `/admin/venues/:id`, whose URL carries no region
  // slug): re-bases the initial draft too, so filling in the region the venue
  // already implies never reads as an operator edit.
  const adoptVenueRegion = (regionId: string) => {
    initialDraft.current = { ...initialDraft.current, regionId };
    setDraft((d) => (d.regionId === regionId ? d : { ...d, regionId }));
    clearError('regionId');
  };

  const { dateError, dateWarning, minDate } = useScheduleErrors(draft.schedule);
  const seriesPreview = useMemo(() => seriesPreviewFor(draft.schedule), [draft.schedule]);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initialDraft.current),
    [draft],
  );
  const { validateStep, stepReachable } = makeStepNav({ step, draft, setFieldErrors });

  const submit = makeSubmit({
    draft,
    mutation,
    keepRegion: regionMode !== 'open',
    keepVenue: venueMode === 'fixed',
    onCreated,
    initialDraft,
    setDraft,
    setFieldErrors,
    setSubmitError,
    setStep,
    setConfirmation,
  });

  return {
    step,
    setStep,
    draft,
    regionId: draft.regionId,
    regionMode,
    venueMode,
    ...editors,
    adoptVenueRegion,
    fieldError: (key) => fieldErrors[key] || undefined,
    validateStep,
    stepReachable,
    dirty,
    dateError,
    dateWarning,
    minDate,
    seriesPreview,
    submit,
    submitting: mutation.isPending,
    submitError,
    confirmation,
  };
}
